import { useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { Button } from '@/components/ui/button'
import { GTDialog, GTDialogBody, GTDialogFooter } from '@/components/ui/gt-dialog'
import { cn } from '@/lib/utils'
import { AlertTriangle, ArrowRight, Loader2, Sparkles } from 'lucide-react'
import {
    GOAL_DRAFT_UNCHANGED_MESSAGE,
    TGoalDraftError,
    TGoalDraftErrorCode,
    TGoalDraftRevision,
    TScaffoldDraftInput,
    buildScaffoldReviewDraft,
    draftGoalPaths,
} from './creation/scaffold/scaffoldDraft'
import PlanDraftingState from './creation/shared/PlanDraftingState'
import PlanFeedback, { PlanFeedbackInvite, focusPlanFeedback } from './creation/shared/PlanFeedback'
import StartingPointPanel from './creation/shared/StartingPointPanel'
import { NO_CAPACITY_LABEL, NO_TIMEFRAME_LABEL, capacityLabelFor } from './creation/shared/propertyOptions'
import {
    TDraftPlanItem,
    TScenarioPath,
    buildGoalFromDraft,
    plannedTaskTitlesForDraft,
} from './creation/shared/scenario'
import { TGoalStartingPoint, planningCommitmentReview } from './creation/shared/startingPoint'
import { TGoal, TGoalPatch, formatGoalAmount } from './goalTypes'

interface GoalRedraftDialogProps {
    goal: TGoal
    /** Titles of the recurring templates hooked to this goal — the repeats Accept leaves alone. */
    repeatTitles: string[]
    /** One-off titles already linked to this goal; preview and Accept skip repeats together. */
    existingTaskTitles: string[]
    open: boolean
    onOpenChange: (open: boolean) => void
    /**
     * The plan fields to save, plus the titles of the accepted plan's one-offs. Those are not
     * goal metadata — a one-off is work — so the host creates and links them as tasks. Without
     * that, accepting a revised plan would drop every one-off the planner returned.
     */
    onAccept: (patch: TGoalPatch, planTaskTitles: string[]) => void
    isSaving?: boolean
}

type TRedraftState =
    | { status: 'idle' | 'loading' }
    /** The raw path rides along with the candidate: it is what feedback critiques. */
    | { status: 'ready'; candidate: TGoal; items: TDraftPlanItem[]; path: TScenarioPath; planTaskTitles: string[] }
    | { status: 'error'; error: TGoalDraftError }

const ERROR_COPY: Record<TGoalDraftErrorCode, { title: string; detail: string }> = {
    no_key: { title: 'AI setup missing', detail: 'No draft was created.' },
    subscription: { title: 'Subscription needed', detail: 'Redraft needs product access.' },
    model: { title: 'Planner error', detail: 'Try again in a bit.' },
    timeout: { title: 'Planner timed out', detail: 'Try again with the same goal.' },
    empty_plan: { title: 'No plan returned', detail: 'The saved plan is unchanged.' },
    unchanged_plan: { title: GOAL_DRAFT_UNCHANGED_MESSAGE, detail: 'The current candidate is still on screen.' },
    network: { title: 'Network error', detail: 'Check the connection and try again.' },
    canceled: { title: 'Redraft canceled', detail: 'The saved plan is unchanged.' },
}

/**
 * The saved pace read back as a capacity pick. Going through the picker's own formatter is what makes
 * the round-trip hold: the label a redraft states is by construction one the capacity pill can show
 * and re-select, whatever number the goal carries.
 */
const capacityLabelForGoal = (goal: TGoal) =>
    goal.weeklyTarget
        ? capacityLabelFor(goal.weeklyTarget, goal.unit === 'hrs' ? 'hrs' : 'sessions')
        : NO_CAPACITY_LABEL

const draftInputForGoal = (goal: TGoal, startingPoint?: TGoalStartingPoint): TScaffoldDraftInput => ({
    title: goal.title,
    why: goal.why ?? '',
    timeframeLabel: goal.timeframeLabel || NO_TIMEFRAME_LABEL,
    capacityLabel: capacityLabelForGoal(goal),
    startingPoint,
})

const taskTitleKey = (title: string) => title.trim().toLowerCase()

const newPlanTaskTitles = (titles: string[], existingTitles: string[]) => {
    const seen = new Set(existingTitles.map(taskTitleKey))
    return titles.filter((title) => {
        const key = taskTitleKey(title)
        if (!key || seen.has(key)) return false
        seen.add(key)
        return true
    })
}

const formatPace = (goal: Pick<TGoal, 'weeklyTarget' | 'unit' | 'progressMode'>) => {
    if (!goal.weeklyTarget || goal.progressMode === 'manual') return 'Self-graded'
    const target = formatGoalAmount(goal.weeklyTarget)
    return goal.unit === 'hrs' ? `${target} hrs / week` : `${target}× / week`
}

const formatType = (goal: Pick<TGoal, 'goalType'>) => (goal.goalType === 'time' ? 'Time-based' : 'Consistency')

const planPatchForCandidate = (current: TGoal, candidate: TGoal): TGoalPatch => {
    const patch: TGoalPatch = {
        targetLabel: candidate.targetLabel,
        progressMode: candidate.progressMode,
        goalType: candidate.goalType,
        phases: candidate.phases ?? [],
        planCadences: candidate.planCadences ?? [],
        weeklyTarget: candidate.weeklyTarget ?? 0,
        unit: candidate.unit ?? '',
    }
    if (
        (candidate.weeklyTarget ?? 0) !== (current.weeklyTarget ?? 0) ||
        (candidate.unit ?? '') !== (current.unit ?? '')
    ) {
        patch.forgive = true
    }
    return patch
}

const DiffRow = ({ label, before, after }: { label: string; before: string; after: string }) => {
    const changed = before !== after
    return (
        <div className="grid grid-cols-[96px_minmax(0,1fr)] gap-3 rounded-gt-md px-2 py-2 text-body-sm">
            <span className="text-label-md uppercase tracking-normal text-muted-foreground">{label}</span>
            <div className="flex min-w-0 items-center gap-2">
                <span
                    className={cn(
                        'min-w-0 truncate',
                        changed ? 'text-muted-foreground line-through' : 'text-foreground'
                    )}
                >
                    {before || 'None'}
                </span>
                {changed && (
                    <>
                        <ArrowRight className="size-3.5 shrink-0 text-muted-foreground" />
                        <span className="min-w-0 truncate font-medium text-foreground">{after || 'None'}</span>
                    </>
                )}
            </div>
        </div>
    )
}

/**
 * What the redraft proposes, read against the repeats the goal actually has.
 *
 * This replaces a diff of `goal.contributors` — two lists of labels the server could never credit,
 * reconciled by string comparison. Accept writes plan fields only; it does not create or hook a
 * recurring template, so a drafted cadence is shown as a suggestion rather than as something the
 * button will silently do. Hooking stays where it can be seen: the Repeats section.
 */
const PlanDiff = ({
    items,
    repeatTitles,
    startingPoint,
}: {
    items: TDraftPlanItem[]
    repeatTitles: string[]
    startingPoint?: TGoalStartingPoint
}) => {
    const included = items.filter((item) => item.included)
    const cadenceLabel = (item: TDraftPlanItem) =>
        item.frequencyLabel ? `${item.title} · ${item.frequencyLabel}` : item.title
    const rows: Array<{ label: string; tag: string; tone: string; detail?: string }> = [
        ...(startingPoint?.commitments.map((commitment) => {
            const review = planningCommitmentReview(commitment, 'redraft')
            return {
                label: commitment.title,
                tag: review.inclusionLabel,
                tone:
                    commitment.source === 'recurring' && commitment.reused
                        ? 'bg-success/15 text-success'
                        : 'bg-muted text-muted-foreground',
                detail: [review.sourceLabel, review.relationLabel, review.changeLabel].filter(Boolean).join(' · '),
            }
        }) ?? []),
        ...repeatTitles
            .filter(
                (title) =>
                    !startingPoint?.commitments.some(
                        (commitment) => commitment.source === 'recurring' && commitment.title === title
                    )
            )
            .map((title) => ({
                label: title,
                tag: 'Keep',
                tone: 'bg-muted text-muted-foreground',
            })),
        ...included
            .filter((item) => item.kind === 'cadence')
            .map((item) => ({
                label: cadenceLabel(item),
                tag: 'Suggested',
                tone: 'bg-success/15 text-success',
            })),
        ...included
            .filter((item) => item.kind === 'milestone')
            .map((item) => ({ label: item.title, tag: 'Milestone', tone: 'bg-muted text-muted-foreground' })),
    ]

    return (
        <section className="space-y-1.5">
            <span className="px-2 text-label-md uppercase tracking-normal text-muted-foreground">Plan</span>
            <div className="space-y-1">
                {rows.length === 0 ? (
                    <p className="px-2 text-body-sm text-muted-foreground">No plan items.</p>
                ) : (
                    rows.map((row) => (
                        <div
                            key={`${row.tag}-${row.label}`}
                            className="flex items-center gap-2 rounded-gt-md px-2 py-1.5"
                        >
                            <span className={cn('shrink-0 rounded-sm px-1.5 text-label-sm', row.tone)}>{row.tag}</span>
                            <span className="min-w-0 flex-1 truncate text-body-sm text-foreground">{row.label}</span>
                            {row.detail && (
                                <span className="shrink-0 text-label-sm text-muted-foreground">{row.detail}</span>
                            )}
                        </div>
                    ))
                )}
            </div>
        </section>
    )
}

const PhasePreview = ({ candidate }: { candidate: TGoal }) => {
    if (!candidate.phases || candidate.phases.length <= 1) return null
    return (
        <section className="space-y-1.5">
            <span className="px-2 text-label-md uppercase tracking-normal text-muted-foreground">Phases</span>
            <div className="flex gap-1">
                {candidate.phases.map((phase) => (
                    <div
                        key={phase.name}
                        style={{ flexGrow: phase.weeks }}
                        className="min-w-0 basis-0 rounded-md bg-muted px-2 py-1"
                    >
                        <p className="truncate text-body-sm font-medium text-foreground">{phase.name}</p>
                        <p className="truncate text-body-sm text-muted-foreground">{phase.cadenceLabel}</p>
                    </div>
                ))}
            </div>
        </section>
    )
}

/**
 * One-offs never reach `contributors`, so a contributor diff cannot show them. Accept turns
 * them into linked tasks, and the user should read that before accepting rather than after.
 */
const PlanTaskPreview = ({ titles }: { titles: string[] }) => {
    if (titles.length === 0) return null
    return (
        <section className="space-y-1.5">
            <span className="px-2 text-label-md uppercase tracking-normal text-muted-foreground">New tasks</span>
            <div className="space-y-1">
                {titles.map((title) => (
                    <div key={title} className="flex items-center gap-2 rounded-gt-md px-2 py-1.5">
                        <span className="shrink-0 rounded-sm bg-success/15 px-1.5 text-label-sm text-success">Add</span>
                        <span className="min-w-0 truncate text-body-sm text-foreground">{title}</span>
                    </div>
                ))}
            </div>
        </section>
    )
}

const GoalRedraftDialog = ({
    goal,
    repeatTitles,
    existingTaskTitles,
    open,
    onOpenChange,
    onAccept,
    isSaving,
}: GoalRedraftDialogProps) => {
    const [state, setState] = useState<TRedraftState>({ status: 'idle' })
    const [retryNonce, setRetryNonce] = useState(0)
    const [isRevising, setIsRevising] = useState(false)
    const [baseGoal, setBaseGoal] = useState<TGoal | null>(null)
    const [startingPoint, setStartingPoint] = useState<TGoalStartingPoint>()
    // Counts applied notes. PlanFeedback is keyed on it so an applied note clears itself while
    // a failed one stays in the box, rather than making the user type it again.
    const [appliedNotes, setAppliedNotes] = useState(0)
    const reviseAbort = useRef<AbortController | null>(null)
    const feedbackRef = useRef<HTMLTextAreaElement>(null)
    const dialogGoal = baseGoal ?? goal
    const planTaskTitlesForAccept = useMemo(
        () => (state.status === 'ready' ? newPlanTaskTitles(state.planTaskTitles, existingTaskTitles) : []),
        [existingTaskTitles, state]
    )

    const draftInput = useMemo<TScaffoldDraftInput>(
        () => draftInputForGoal(dialogGoal, startingPoint),
        [
            dialogGoal.id,
            dialogGoal.title,
            dialogGoal.why,
            dialogGoal.timeframeLabel,
            dialogGoal.weeklyTarget,
            dialogGoal.unit,
            startingPoint,
        ]
    )

    useEffect(() => {
        if (open) setBaseGoal((current) => current ?? goal)
        else {
            setBaseGoal(null)
            setStartingPoint(undefined)
        }
    }, [goal, open])

    useEffect(() => {
        if (!open) {
            setState({ status: 'idle' })
            reviseAbort.current?.abort()
            setIsRevising(false)
            return
        }
        if (!startingPoint) return

        let canceled = false
        const controller = new AbortController()
        // A fresh draft supersedes any revision still in flight.
        reviseAbort.current?.abort()
        setIsRevising(false)
        setState({ status: 'loading' })
        void draftGoalPaths(draftInput, controller.signal).then((result) => {
            if (canceled) return
            if (result.status === 'ready') {
                const draft = buildScaffoldReviewDraft(draftInput, result.paths)
                setState({
                    status: 'ready',
                    candidate: buildGoalFromDraft(draft),
                    items: draft.items,
                    path: result.paths[0],
                    planTaskTitles: plannedTaskTitlesForDraft(draft),
                })
            } else if (result.error.code !== 'canceled') {
                setState({ status: 'error', error: result.error })
            }
        })

        return () => {
            canceled = true
            controller.abort()
        }
    }, [draftInput, open, retryNonce, startingPoint])

    /**
     * A revision runs underneath the diff rather than replacing it: the plan the note was
     * about stays on screen, and a planner failure costs a toast instead of the candidate.
     */
    const revise = (feedback: string) => {
        if (state.status !== 'ready' || isRevising) return
        const revision: TGoalDraftRevision = { feedback, currentPlan: state.path }
        const controller = new AbortController()
        reviseAbort.current?.abort()
        reviseAbort.current = controller
        setIsRevising(true)
        void draftGoalPaths(draftInput, controller.signal, revision)
            .then((result) => {
                if (controller.signal.aborted) return
                if (result.status === 'ready') {
                    const draft = buildScaffoldReviewDraft(draftInput, result.paths)
                    setState({
                        status: 'ready',
                        candidate: buildGoalFromDraft(draft),
                        items: draft.items,
                        path: result.paths[0],
                        planTaskTitles: plannedTaskTitlesForDraft(draft),
                    })
                    setAppliedNotes((applied) => applied + 1)
                } else if (result.error.code !== 'canceled') {
                    toast.error(ERROR_COPY[result.error.code].title)
                }
            })
            .finally(() => {
                if (!controller.signal.aborted) setIsRevising(false)
            })
    }

    const accept = () => {
        if (state.status !== 'ready') return
        onAccept(planPatchForCandidate(dialogGoal, state.candidate), planTaskTitlesForAccept)
        toast.success('Plan updated')
        onOpenChange(false)
    }

    return (
        <GTDialog
            open={open}
            onOpenChange={onOpenChange}
            label="Redraft plan"
            description="Review the drafted plan before accepting it."
            size="wide"
        >
            {!startingPoint && (
                <StartingPointPanel
                    title={dialogGoal.title}
                    goalId={dialogGoal.id}
                    capacityLabel={capacityLabelForGoal(dialogGoal)}
                    initialBaseline={dialogGoal.progressLabel}
                    onCancel={() => onOpenChange(false)}
                    onConfirm={(nextStartingPoint) => {
                        setState({ status: 'loading' })
                        setStartingPoint(nextStartingPoint)
                    }}
                />
            )}
            {startingPoint && state.status === 'loading' && (
                <>
                    <GTDialogBody className="gap-5">
                        <PlanDraftingState title="Drafting plan" subtitle="The saved goal stays unchanged." />
                    </GTDialogBody>
                    <GTDialogFooter onCancel={() => onOpenChange(false)} cancelLabel="Discard" />
                </>
            )}

            {startingPoint && state.status === 'error' && (
                <>
                    <GTDialogBody className="gap-4">
                        <div className="flex items-start gap-3 rounded-gt-md border border-border bg-card p-3">
                            <div className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                                <AlertTriangle className="size-4" />
                            </div>
                            <div className="min-w-0">
                                <p className="text-body-sm font-medium text-foreground">
                                    {ERROR_COPY[state.error.code].title}
                                </p>
                                <p className="mt-0.5 text-body-sm text-muted-foreground">
                                    {ERROR_COPY[state.error.code].detail}
                                </p>
                            </div>
                        </div>
                    </GTDialogBody>
                    <GTDialogFooter onCancel={() => onOpenChange(false)} cancelLabel="Discard">
                        <Button size="sm" variant="outline" onClick={() => setRetryNonce((n) => n + 1)}>
                            Retry
                        </Button>
                    </GTDialogFooter>
                </>
            )}

            {startingPoint && state.status === 'ready' && (
                <>
                    <GTDialogBody className="gap-5">
                        <div className="space-y-2">
                            <div className="flex items-center gap-2 text-body-sm text-muted-foreground">
                                <Sparkles className="size-4 text-primary" />
                                <span>Review changes before they replace the saved plan.</span>
                            </div>
                            <p className="text-body-sm text-muted-foreground">
                                Tasks stay linked. Repeats stay hooked. Calendar events stay unchanged.
                            </p>
                            <PlanFeedbackInvite onClick={() => focusPlanFeedback(feedbackRef.current)} />
                        </div>

                        <section className="space-y-1 rounded-gt-md border border-border bg-card py-1">
                            <DiffRow label="Pace" before={formatPace(dialogGoal)} after={formatPace(state.candidate)} />
                            <DiffRow
                                label="Target"
                                before={dialogGoal.targetLabel}
                                after={state.candidate.targetLabel}
                            />
                            <DiffRow
                                label="Timeframe"
                                before={dialogGoal.timeframeLabel || 'Open-ended'}
                                after={dialogGoal.timeframeLabel || 'Open-ended'}
                            />
                            <DiffRow label="Type" before={formatType(dialogGoal)} after={formatType(state.candidate)} />
                        </section>

                        <PlanDiff items={state.items} repeatTitles={repeatTitles} startingPoint={startingPoint} />
                        <PhasePreview candidate={state.candidate} />
                        <PlanTaskPreview titles={planTaskTitlesForAccept} />
                    </GTDialogBody>
                    <PlanFeedback key={appliedNotes} ref={feedbackRef} onRevise={revise} isRevising={isRevising} />
                    <GTDialogFooter
                        onCancel={() => onOpenChange(false)}
                        cancelLabel="Discard"
                        onConfirm={accept}
                        confirmLabel={
                            <span className="inline-flex items-center gap-1.5">
                                {isSaving && <Loader2 className="size-3.5 animate-spin" />}
                                Accept
                            </span>
                        }
                        confirmDisabled={isSaving || isRevising}
                    />
                </>
            )}
        </GTDialog>
    )
}

export default GoalRedraftDialog
